/**
 * Filter + bulk payload builders for modular price-list property edits.
 */
import { cloneDeep } from 'lodash';
import {
    buildCategoryAssignmentsFromSystems,
    DEFAULT_SYSTEM_SUBCATEGORY,
    getProductMainCategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import { isAnulatProductStatus } from '@/data/productStatus';
import {
    createDraftEntry,
    resolveDraftAction,
    resolveExchangeRateRecalcPrevious,
} from '@/utils/priceListDraftQueue';
import {
    getBulkField,
    formatBulkEditValue,
    formatCurrentFieldValue,
    formatSupplierListPriceBulkLabel,
    isBulkEditValueUnchanged,
    itemMatchesBulkFieldFilter,
    normalizeBulkEditValue,
    resolveItemMarkupActualRatio,
    resolveItemMarkupRatio,
    subcategoryForSystem,
    systemsFromItem,
    getItemFurnizorFilterLabels,
} from '@/utils/priceListBulkFields';
import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    DEFAULT_PRICING_SETTINGS,
    hydrateDiscountsFromLegacy,
    parseNumericInput,
    percentageInputToRatio,
    persistActiveFurnizorPricingFromRoot,
    roundMoney,
    syncSystemsListToLegacy,
    formatNumericInputValue,
} from '@/utils/priceListProductCalculator';
import {
    applyExchangeRateChangeKeepingMarkup,
    applyPricingFieldEdit,
    applySupplierListPricePercentChangeKeepingMarkup,
    clearListPriceOverride,
} from '@/utils/priceListPricingFieldSync';
import { normalizeCurrencyCode } from '@/utils/transportCostBreakdown';
import {
    buildDraftPricingBaselinesFromPreEditForm,
    mergeDraftPricingBaselinesWithExisting,
} from '@/utils/priceListPricingFieldBaselines';
import {
    builderFormToSavePayload,
    legacyProductToBuilderForm,
    pricingDetailToBuilderForm,
} from '@/utils/pricingDetailMapper';
import { parseProductCodeStructure } from '@/utils/productStructureHelpers';
import { includesFolded } from '@/utils/textSearch';
import { getManufacturerCodes } from '@/data/manufacturerCodes';
import {
    activateMatchingFurnizorTab,
    buildPropagatePeerSearchHaystack,
    getFurnizorIdentity,
    getPricingMatchLabels,
    resolvePropagatePeerItem,
} from '@/utils/priceListPropagateFurnizor';

/** Fields that must go through applyPricingFieldEdit after the form value is set. */
const BULK_PRICING_FIELD_EDIT_KEYS = new Set([
    'markupActual',
    'supplierListPriceNew',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'stampDutyAmount',
]);

/**
 * Selling price (RON cu TVA) to keep sticky during bulk upstream edits —
 * same role as builder `listPriceIncVatFocusSnapshot`.
 * Without this, products without an override flag recalculate list price from
 * entry (e.g. discount / transport edits would collapse pret vânzare).
 * Note: pret listă furnizor % majorare uses keep-markup (like FX), not sticky.
 *
 * @param {Record<string, unknown>} form
 * @param {Object} item
 * @returns {number|null}
 */
function resolveBulkPreservedListPrice(form, item) {
    const candidates = [
        form?.overrideListPriceRonIncVatFinal
            ? form.overrideListPriceRonIncVatFinalValue
            : null,
        form?.listPriceRonIncVatFinal,
        item?.price,
    ];
    for (const candidate of candidates) {
        const value = parseNumericInput(candidate);
        if (value > 0) {
            return value;
        }
    }
    return null;
}

/**
 * Scale current selling price when adaos actual changes:
 * newPrice = current × (1 + newAdaos) / (1 + oldAdaos).
 * Avoids rewriting list price from incomplete entry-cost data in bulk.
 *
 * @param {number} currentPrice
 * @param {number|null|undefined} oldMarkupRatio
 * @param {number|null|undefined} newMarkupRatio
 * @returns {number|null}
 */
/** Same 2-decimal % precision as bulk "Valoare curentă" / user input. */
function markupActualPercentsEqual(leftRatio, rightRatio) {
    if (leftRatio == null || rightRatio == null) {
        return false;
    }
    if (!Number.isFinite(leftRatio) || !Number.isFinite(rightRatio)) {
        return false;
    }
    return roundMoney(leftRatio * 100, 2) === roundMoney(rightRatio * 100, 2);
}

export function scaleListPriceByMarkupActualChange(
    currentPrice,
    oldMarkupRatio,
    newMarkupRatio,
) {
    if (!(currentPrice > 0)) {
        return null;
    }
    if (oldMarkupRatio == null || !Number.isFinite(oldMarkupRatio) || oldMarkupRatio <= -0.999999) {
        return null;
    }
    if (newMarkupRatio == null || !Number.isFinite(newMarkupRatio)) {
        return null;
    }
    // Same adaos at UI precision → keep price byte-for-byte (no re-round drift).
    if (markupActualPercentsEqual(oldMarkupRatio, newMarkupRatio)) {
        return currentPrice;
    }
    return roundMoney(
        currentPrice * ((1 + newMarkupRatio) / (1 + oldMarkupRatio)),
        2,
    );
}

/**
 * Apply bulk adaos actual: keep selling-price continuity by scaling the current
 * list price, then lock it as sticky override while storing the new adaos %.
 *
 * @param {Record<string, unknown>} form
 * @param {Object} item
 * @param {unknown} normalizedMarkupValue percentage-form value (e.g. "36")
 * @returns {boolean} true when proportional sticky path was applied
 */
/**
 * Selling price shown in the price-list / bulk preview ("Preț vânzare vechi cu TVA").
 * Prefer this over hydrated form list price — form can drift by ±0.01 vs item.price.
 * @param {Object} item
 * @param {Record<string, unknown>} form
 * @returns {number|null}
 */
function resolveBulkMarkupListPrice(item, form) {
    const fromItem = parseNumericInput(item?.price);
    if (fromItem > 0) {
        return fromItem;
    }
    return resolveBulkPreservedListPrice(form, item);
}

/**
 * Force payload list price after builderFormToSavePayload — that helper recomputes
 * from the default furnizor tab and can drift sticky prices by ±0.01.
 * @param {Object} payload
 * @param {number} listPrice
 */
function lockPayloadListPrice(payload, listPrice) {
    const price = roundMoney(parseNumericInput(listPrice), 2);
    if (!(price > 0) || !payload) {
        return;
    }
    // Store plain numbers in payload (not ro-RO "1.234,56") so publish/recalc
    // never mis-parse thousands vs decimals.
    payload.price = price;
    if (!payload.pricing_detail) {
        return;
    }
    payload.pricing_detail.listPriceRonIncVatFinal = price;
    payload.pricing_detail.overrideListPriceRonIncVatFinal = true;
    payload.pricing_detail.overrideListPriceRonIncVatFinalValue = price;
    if (!Array.isArray(payload.pricing_detail.furnizorPricings)) {
        return;
    }
    payload.pricing_detail.furnizorPricings = payload.pricing_detail.furnizorPricings.map(
        (row) => (
            row?.isDefault
                ? {
                    ...row,
                    listPriceRonIncVatFinal: price,
                    overrideListPriceRonIncVatFinal: true,
                    overrideListPriceRonIncVatFinalValue: price,
                }
                : row
        ),
    );
}

function applyBulkMarkupActualKeepingListPrice(form, item, normalizedMarkupValue) {
    const currentPrice = resolveBulkMarkupListPrice(item, form);
    const oldRatio = resolveItemMarkupActualRatio(item)
        ?? (
            String(form?.markupActual ?? '').trim() !== ''
                ? percentageInputToRatio(form.markupActual)
                : null
        );
    const newRatio = percentageInputToRatio(normalizedMarkupValue);

    // Identical adaos at display precision: keep sticky/list price as-is.
    if (markupActualPercentsEqual(oldRatio, newRatio) && currentPrice > 0) {
        form.markupActual = formatNumericInputValue(parseNumericInput(normalizedMarkupValue), 2);
        form.overrideListPriceRonIncVatFinal = true;
        form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(currentPrice, 2);
        form.useManualFinalPrice = false;
        form.manualListPriceRonIncVatFinal = '';
        form.priceAdjustmentRatio = '';
        return true;
    }

    const scaledPrice = scaleListPriceByMarkupActualChange(
        currentPrice,
        oldRatio,
        newRatio,
    );

    form.markupActual = formatNumericInputValue(parseNumericInput(normalizedMarkupValue), 2);

    if (scaledPrice != null) {
        form.overrideListPriceRonIncVatFinal = true;
        form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(scaledPrice, 2);
        form.useManualFinalPrice = false;
        form.manualListPriceRonIncVatFinal = '';
        form.priceAdjustmentRatio = '';
        return true;
    }

    // No reliable old adaos / current price → fall back to formula from entry cost.
    clearListPriceOverride(form);
    return false;
}

/**
 * Activate the furnizor pricing tab that matches a display label (bulk filter).
 * @param {Record<string, unknown>} form
 * @param {unknown} furnizorLabel
 * @returns {boolean}
 */
function activateFurnizorTabByLabel(form, furnizorLabel) {
    const wanted = String(furnizorLabel || '').trim().toLowerCase();
    if (!wanted) {
        return false;
    }
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    const context = {
        manufacturerCode: form?.manufacturerCode,
        manufacturerName: form?.manufacturerName,
    };
    for (const pricing of pricings) {
        const identity = getFurnizorIdentity(pricing, context);
        const labels = getPricingMatchLabels(pricing, context);
        const matches = labels.some((label) => String(label).trim().toLowerCase() === wanted)
            || String(identity?.label || '').trim().toLowerCase() === wanted;
        if (matches && identity) {
            return Boolean(activateMatchingFurnizorTab(form, identity));
        }
    }
    return false;
}

/**
 * Resolve manufacturer code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemManufacturerCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.manufacturerCode
        || item?.manufacturer_code
        || item?.manufacturerCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.manufacturerCode || '').trim();
}

/**
 * Resolve manufacturer name from pricing detail or product fields.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemManufacturerName(item) {
    return String(
        item?.pricing_detail?.manufacturerName
        || item?.manufacturer_name
        || item?.manufacturerName
        || item?.furnizor
        || '',
    ).trim();
}

/**
 * Resolve chapter code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemChapterCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.chapterCode
        || item?.chapter_code
        || item?.chapterCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.chapterCode || '').trim();
}

/**
 * Resolve subchapter code from pricing detail or product code structure.
 * @param {Object} item
 * @returns {string}
 */
export function resolveItemSubchapterCode(item) {
    const fromDetail = String(
        item?.pricing_detail?.subchapterCode
        || item?.subchapter_code
        || item?.subchapterCode
        || '',
    ).trim();
    if (fromDetail) {
        return fromDetail;
    }
    const parsed = parseProductCodeStructure(item?.product_code);
    return String(parsed?.subchapterCode || '').trim();
}

/**
 * Normalize manufacturer codes for comparison ("03" vs "3").
 * @param {unknown} code
 * @returns {string}
 */
function normalizeManufacturerCode(code) {
    const raw = String(code || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    // Keep alphanumeric codes as-is; strip leading zeros only for pure numeric codes.
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * Normalize chapter/subchapter codes for comparison ("08" vs "8", "1" vs "01").
 * @param {unknown} code
 * @returns {string}
 */
function normalizeStructureCode(code) {
    const raw = String(code || '').trim().toLowerCase();
    if (!raw) {
        return '';
    }
    if (/^\d+$/.test(raw)) {
        return String(Number(raw));
    }
    return raw;
}

/**
 * @param {Object} item
 * @param {Record<string, unknown>} criteria
 * @returns {boolean}
 */
export function itemMatchesBulkCriteria(item, criteria = {}) {
    if (!item) {
        return false;
    }

    const search = String(criteria.search || '').trim();
    if (search) {
        // Same fields as price-list list / propagate: product code+name and
        // manufacturer (furnizor) product code+name.
        if (!includesFolded(buildPropagatePeerSearchHaystack(item), search)) {
            return false;
        }
    }

    if (criteria.systems != null && criteria.systems !== '') {
        const wanted = (Array.isArray(criteria.systems) ? criteria.systems : [criteria.systems])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        if (wanted.length) {
            const itemSystems = systemsFromItem(item);
            if (!wanted.some((system) => itemSystems.includes(system))) {
                return false;
            }
        }
    }

    if (criteria.systemSubcategory != null && String(criteria.systemSubcategory).trim()) {
        const wantedSub = String(criteria.systemSubcategory).trim().toLowerCase();
        const filterSystems = (Array.isArray(criteria.systems) ? criteria.systems : [criteria.systems])
            .map((entry) => normalizeSystemValue(entry))
            .filter(Boolean);
        const systemsToCheck = filterSystems.length ? filterSystems : systemsFromItem(item);
        const matched = systemsToCheck.some((system) => {
            const sub = subcategoryForSystem(item, system);
            return String(sub || '').trim().toLowerCase() === wantedSub;
        });
        if (!matched) {
            return false;
        }
    }

    if (criteria.measurementUnit != null && String(criteria.measurementUnit).trim()) {
        if (
            String(item.measurement_unit || '').trim()
            !== String(criteria.measurementUnit).trim()
        ) {
            return false;
        }
    }

    if (criteria.manufacturerCode != null && String(criteria.manufacturerCode).trim()) {
        const wanted = normalizeManufacturerCode(criteria.manufacturerCode);
        const itemCode = normalizeManufacturerCode(resolveItemManufacturerCode(item));
        if (!itemCode || itemCode !== wanted) {
            return false;
        }
    }

    if (criteria.manufacturerName != null && String(criteria.manufacturerName).trim()) {
        const name = resolveItemManufacturerName(item);
        if (!includesFolded(name, criteria.manufacturerName)) {
            return false;
        }
    }

    if (criteria.furnizorLabel != null && String(criteria.furnizorLabel).trim()) {
        const wanted = String(criteria.furnizorLabel).trim().toLowerCase();
        const labels = getItemFurnizorFilterLabels(item).map(
            (label) => String(label).trim().toLowerCase(),
        );
        if (!labels.includes(wanted)) {
            return false;
        }
    }

    if (criteria.chapterCode != null) {
        const wantedChapters = (
            Array.isArray(criteria.chapterCode) ? criteria.chapterCode : [criteria.chapterCode]
        )
            .map((entry) => normalizeStructureCode(entry))
            .filter(Boolean);
        if (wantedChapters.length) {
            const chapter = normalizeStructureCode(resolveItemChapterCode(item));
            if (!chapter || !wantedChapters.includes(chapter)) {
                return false;
            }
        }
    }

    if (criteria.subchapterCode != null && String(criteria.subchapterCode).trim()) {
        const wanted = normalizeStructureCode(criteria.subchapterCode);
        const subchapter = normalizeStructureCode(resolveItemSubchapterCode(item));
        if (!subchapter || subchapter !== wanted) {
            return false;
        }
    }

    const handledKeys = new Set([
        'search',
        'systems',
        'systemSubcategory',
        'measurementUnit',
        'manufacturerCode',
        'manufacturerName',
        'furnizorLabel',
        'chapterCode',
        'subchapterCode',
    ]);
    for (const [key, wanted] of Object.entries(criteria)) {
        if (handledKeys.has(key)) {
            continue;
        }
        const field = getBulkField(key);
        if (!field) {
            continue;
        }
        if (!itemMatchesBulkFieldFilter(item, field, wanted, criteria)) {
            return false;
        }
    }

    return true;
}

/**
 * @param {Array<Object>} items
 * @param {Record<string, unknown>} criteria
 * @returns {Array<Object>}
 */
export function filterItemsForBulkEdit(items, criteria = {}) {
    return (items || []).filter((item) => itemMatchesBulkCriteria(item, criteria));
}

/**
 * @param {Object} form
 * @param {Object} field
 * @param {unknown} normalizedValue
 */
function applyFieldToBuilderForm(form, field, normalizedValue) {
    switch (field.key) {
        case 'productName':
            form.productName = normalizedValue;
            break;
        case 'manufacturerProductName':
            form.manufacturerProductName = normalizedValue;
            (form.furnizorPricings || []).forEach((pricing) => {
                pricing.manufacturerProductName = normalizedValue;
            });
            break;
        case 'manufacturerProductCode':
            form.manufacturerProductCode = normalizedValue;
            (form.furnizorPricings || []).forEach((pricing) => {
                pricing.manufacturerProductCode = normalizedValue;
            });
            break;
        case 'measurementUnit':
            form.measurementUnit = normalizedValue;
            break;
        case 'weight':
            form.weight = normalizedValue;
            break;
        case 'productNotes':
            form.productNotes = normalizedValue;
            break;
        case 'nomenclatureCode':
            form.nomenclatureCode = normalizedValue;
            break;
        case 'serialNumber':
            form.serialNumber = Boolean(normalizedValue);
            break;
        case 'isCompositeProduct':
            form.isCompositeProduct = Boolean(normalizedValue);
            break;
        case 'visibleInOfferApp':
            form.visibleInOfferApp = isAnulatProductStatus(form.status)
                ? false
                : Boolean(normalizedValue);
            break;
        case 'status':
            form.status = normalizedValue || null;
            if (isAnulatProductStatus(form.status)) {
                form.visibleInOfferApp = false;
            }
            break;
        case 'systems': {
            const systems = Array.isArray(normalizedValue) ? normalizedValue : [];
            form.systems = systems.length ? [...systems] : [''];
            form.systemSubcategories = form.systems.map(
                (_system, index) => form.systemSubcategories?.[index]
                    || DEFAULT_SYSTEM_SUBCATEGORY,
            );
            syncSystemsListToLegacy(form);
            break;
        }
        case 'systemSubcategory': {
            const subcategory = String(normalizedValue || '').trim();
            const systems = Array.isArray(form.systems) && form.systems.length
                ? form.systems
                : [''];
            form.systemSubcategories = systems.map(() => subcategory || DEFAULT_SYSTEM_SUBCATEGORY);
            syncSystemsListToLegacy(form);
            break;
        }
        case 'chapterCode':
            form.chapterCode = normalizedValue;
            break;
        case 'subchapterCode':
            form.subchapterCode = normalizedValue;
            break;
        case 'manufacturerCode': {
            form.manufacturerCode = normalizedValue;
            const manufacturers = getManufacturerCodes();
            const match = manufacturers.find(
                (entry) => String(entry.code).trim() === String(normalizedValue || '').trim(),
            );
            if (match?.name) {
                form.manufacturerName = match.name;
            }
            break;
        }
        case 'markupReference':
            form.markupReference = normalizedValue;
            break;
        case 'markupActual':
            form.markupActual = normalizedValue;
            break;
        case 'transportCostRate':
            form.transportCostRate = normalizedValue;
            break;
        case 'transportCostEstimated':
            form.transportCostEstimated = normalizedValue;
            break;
        case 'discount1':
            form.discount1 = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'discount2':
            form.discount2 = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'cashDiscount':
            form.cashDiscount = normalizedValue;
            hydrateDiscountsFromLegacy(form);
            break;
        case 'marketingDiscount':
            form.marketingDiscount = normalizedValue;
            break;
        case 'currency':
            form.currency = normalizedValue;
            break;
        case 'supplierListPriceOld':
            form.supplierListPriceOld = normalizedValue;
            break;
        case 'supplierListPriceNew':
            form.supplierListPriceNew = normalizedValue;
            break;
        case 'paymentTermDays':
            form.paymentTermDays = normalizedValue;
            break;
        case 'notes':
            form.notes = normalizedValue;
            break;
        case 'discountNotes':
            form.discountNotes = normalizedValue ? [normalizedValue] : [];
            break;
        case 'stampDutyAmount':
            form.stampDutyAmount = normalizedValue;
            break;
        default:
            break;
    }
}

/**
 * Policy A: markup drives final price — clear actual markup + reference/override.
 * @param {Object} form
 */
export function applyMarkupRecalcPolicyA(form) {
    form.markupActual = '';
    form.listPriceRonIncVatReference = '';
    form.overrideListPriceRonIncVatFinal = false;
    form.overrideListPriceRonIncVatFinalValue = '';
    form.useManualFinalPrice = false;
    form.manualListPriceRonIncVatFinal = '';
    form.priceAdjustmentRatio = '';
}

/**
 * Normalize bulk edit entries from either a single field/value or an edits array.
 * @param {Object|null} field
 * @param {unknown} value
 * @param {Array<{ fieldKey?: string, field?: Object, value: unknown }>|null} edits
 * @returns {Array<{ field: Object, value: unknown, normalizedValue: unknown }>|null}
 */
function resolveBulkEdits(field, value, edits = null) {
    const rawEdits = Array.isArray(edits) && edits.length
        ? edits
        : (field ? [{ field, value }] : []);
    const resolved = [];
    for (const entry of rawEdits) {
        let resolvedField = entry.field || getBulkField(entry.fieldKey);
        if (!resolvedField) {
            return null;
        }
        let applyMode = entry.applyMode === 'multiplyPercent' ? 'multiplyPercent' : 'set';
        // Dedicated "Majorare" → Majorare costuri (sumă fixă RON/buc). Never touch pret lista.
        if (
            resolvedField.key === 'supplierListPriceMajorare'
            || resolvedField.appliesCostUpliftFixed
        ) {
            applyMode = 'costUpliftFixed';
        } else if (resolvedField.fixedApplyMode === 'multiplyPercent') {
            // Legacy remap: multiply % applied on a target pricing field.
            const targetKey = resolvedField.multiplyTargetFieldKey || 'supplierListPriceNew';
            const targetField = getBulkField(targetKey) || resolvedField;
            resolvedField = targetField;
            applyMode = 'multiplyPercent';
        }
        resolved.push({
            field: resolvedField,
            value: entry.value,
            normalizedValue: normalizeBulkEditValue(resolvedField, entry.value),
            applyMode,
        });
    }
    return resolved.length ? resolved : null;
}

/**
 * Append a fixed Majorare costuri row (RON/buc, type=fixed).
 * Does not change Preț listă furnizor.
 * @param {Object} form
 * @param {unknown} amountValue user-entered fixed amount (e.g. 2.5 for +2,5 RON/buc)
 */
export function applyCostUpliftFixed(form, amountValue) {
    const amount = parseNumericInput(amountValue);
    if (!(amount > 0)) {
        return;
    }
    if (!Array.isArray(form.costUplifts)) {
        form.costUplifts = [];
    }
    if (!Array.isArray(form.costUpliftTypes)) {
        form.costUpliftTypes = [];
    }
    if (!Array.isArray(form.costUpliftNotes)) {
        form.costUpliftNotes = [];
    }
    // Align lengths before push (older rows may miss types/notes).
    while (form.costUpliftTypes.length < form.costUplifts.length) {
        form.costUpliftTypes.push('fixed');
    }
    while (form.costUpliftNotes.length < form.costUplifts.length) {
        form.costUpliftNotes.push('');
    }
    form.costUplifts.push(formatNumericInputValue(amount, 2));
    form.costUpliftTypes.push('fixed');
    form.costUpliftNotes.push('');
}

/**
 * Snapshot pre-edit pricing baselines so bulk drafts reopen in builder with
 * orange highlights and Valoare veche (same shape as manual draft save).
 * @param {Object} preEditForm
 * @param {Object} pricingSettings
 * @param {Record<string, unknown>|null} [existingBuilderState]
 * @returns {ReturnType<typeof buildDraftPricingBaselinesFromPreEditForm>}
 */
export function buildBulkDraftPricingBaselines(
    preEditForm,
    pricingSettings,
    existingBuilderState = null,
) {
    const fresh = buildDraftPricingBaselinesFromPreEditForm(
        preEditForm,
        pricingSettings,
    );
    return mergeDraftPricingBaselinesWithExisting(fresh, existingBuilderState);
}

/**
 * Append an effective-date / majorare note to furnizor notes without wiping existing text.
 * @param {Object} form
 * @param {string} note
 */
function appendEffectiveNoteToForm(form, note) {
    const addition = String(note || '').trim();
    if (!addition) {
        return;
    }
    const existing = String(form.notes || '').trim();
    if (!existing) {
        form.notes = addition;
        return;
    }
    if (existing.includes(addition)) {
        return;
    }
    form.notes = `${existing}\n${addition}`;
}

/**
 * @param {Object} item
 * @param {Object|null} field
 * @param {unknown} value
 * @param {Object} options
 * @returns {{ ok: true, payload: Object, builderState: Object, preview: Object }
 *   | { ok: false, reason: string, item: Object }}
 */
export function buildBulkUpdateForItem(item, field, value, options = {}) {
    const {
        priceListIds = [],
        pricingSettings = DEFAULT_PRICING_SETTINGS,
        edits = null,
        effectiveNote = '',
        furnizorLabel = '',
        /** Locked revert rates for an in-progress FX wave (do not overwrite). */
        exchangeRateRecalcPreviousLock = null,
    } = options;

    if (!item?.product_code) {
        return { ok: false, reason: 'Produs fără cod.', item };
    }

    const resolvedEdits = resolveBulkEdits(field, value, edits);
    if (!resolvedEdits && !String(effectiveNote || '').trim()) {
        return { ok: false, reason: 'Nicio proprietate de actualizat.', item };
    }
    // Skip set-mode edits that already match the product (e.g. adaos 85% → 85%)
    // so sticky list price / calculator side-effects do not rewrite unchanged rows.
    const editsToApply = (resolvedEdits || []).filter((entry) => {
        if (entry.applyMode !== 'set') {
            return true;
        }
        return !isBulkEditValueUnchanged(entry.field, item, entry.normalizedValue);
    });
    if (!editsToApply.length && !String(effectiveNote || '').trim()) {
        return { ok: false, reason: 'Valoarea este neschimbată.', item };
    }
    for (const entry of editsToApply) {
        // List FX rates stay non-editable in the bulk UI, but the dedicated
        // list-level curs recalc still applies them via this helper.
        if (!entry.field?.editable && !entry.field?.isListExchangeRate) {
            return { ok: false, reason: 'Câmpul nu poate fi editat în masă.', item };
        }
        if (
            entry.applyMode === 'multiplyPercent'
            && !entry.field.supportsMultiplyPercent
        ) {
            return {
                ok: false,
                reason: `Majorarea % nu e disponibilă pentru ${entry.field.label}.`,
                item,
            };
        }
        if (
            entry.applyMode === 'costUpliftFixed'
            && !(
                entry.field.key === 'supplierListPriceMajorare'
                || entry.field.appliesCostUpliftFixed
            )
        ) {
            return {
                ok: false,
                reason: `Majorare costuri nu e disponibilă pentru ${entry.field.label}.`,
                item,
            };
        }
    }

    const hasPricingDetail = Boolean(
        item.pricing_detail || item.has_pricing_detail,
    );

    const needsPricingRecalc = editsToApply.some(
        (entry) => entry.field.needsPricingRecalc || entry.field.markupRecalcPolicyA,
    );
    const hasMarkupPolicyA = editsToApply.some(
        (entry) => entry.field.markupRecalcPolicyA,
    );
    const markupPolicyEdit = [...editsToApply]
        .reverse()
        .find((entry) => entry.field.markupRecalcPolicyA) || null;

    const categories = getProductMainCategories(item);
    let productBuilderForm;
    let settings;

    // Prefer lista-în-lucru builder form when present (FX / bulk must not wipe draft edits).
    const draftForm = item._draftEntry?.builderState?.productBuilderForm;
    const existingBuilderState = item._draftEntry?.builderState || null;
    if (draftForm) {
        productBuilderForm = cloneDeep(draftForm);
        settings = item._draftEntry?.builderState?.pricingSettings || {};
    } else if (hasPricingDetail) {
        ({ productBuilderForm, pricingSettings: settings } = pricingDetailToBuilderForm(
            item.pricing_detail,
            item,
        ));
    } else {
        // Upgrade legacy products by synthesizing a builder form and creating pricing_detail.
        ({ productBuilderForm, pricingSettings: settings } = legacyProductToBuilderForm(
            item,
            categories,
        ));
    }

    // When filtering by furnizor, edit that tab — not necessarily the default one.
    if (String(furnizorLabel || '').trim()) {
        activateFurnizorTabByLabel(productBuilderForm, furnizorLabel);
    }

    const previousSettings = {
        ...DEFAULT_PRICING_SETTINGS,
        ...settings,
        ...pricingSettings,
    };
    const mergedSettings = { ...previousSettings };

    const exchangeRateEdits = editsToApply.filter(
        (entry) => entry.field?.isListExchangeRate,
    );
    for (const entry of exchangeRateEdits) {
        const rate = parseNumericInput(entry.normalizedValue ?? entry.value);
        if (rate > 0) {
            mergedSettings[entry.field.key] = rate;
        }
    }

    const bulkDraftBaselines = buildBulkDraftPricingBaselines(
        productBuilderForm,
        previousSettings,
        existingBuilderState,
    );

    if (exchangeRateEdits.length) {
        const affectedCurrencies = currenciesForExchangeRateFields(
            exchangeRateEdits.map((entry) => entry.field?.key).filter(Boolean),
        );
        applyExchangeRateChangeKeepingMarkup(
            productBuilderForm,
            mergedSettings,
            previousSettings,
            {
                fallbackListPrice: item.price,
                affectedCurrencies,
            },
        );
    }

    // Sticky selling price for upstream edits (discounturi, transport, …).
    // Exception: markupActual / FX / supplier-list % keep-markup change list price.
    const preservedListPrice = resolveBulkPreservedListPrice(
        productBuilderForm,
        item,
    );
    const stickyEditOptions = preservedListPrice != null
        ? { preservedListPriceRonIncVatFinal: preservedListPrice }
        : {};

    for (const entry of editsToApply) {
        if (entry.field?.isListExchangeRate) {
            // Already applied above (list-level rates + keep-markup recalc).
            continue;
        }
        if (entry.applyMode === 'costUpliftFixed') {
            applyCostUpliftFixed(productBuilderForm, entry.normalizedValue || entry.value);
            // Same side-effects as builder "Adaugă majorare" (sticky list + markup sync).
            applyPricingFieldEdit(
                productBuilderForm,
                'costUplifts',
                productBuilderForm.costUplifts,
                mergedSettings,
                stickyEditOptions,
            );
        } else if (
            entry.applyMode === 'multiplyPercent'
            && entry.field.key === 'supplierListPriceNew'
        ) {
            // Keep adaos, update selling price — same policy as curs valutar.
            applySupplierListPricePercentChangeKeepingMarkup(
                productBuilderForm,
                entry.value,
                mergedSettings,
                { fallbackListPrice: item.price },
            );
        } else if (entry.field.key === 'currency') {
            // applyPricingFieldEdit expects the previous currency still on the form
            // so it can convert supplier list amounts into the target currency.
            applyPricingFieldEdit(
                productBuilderForm,
                'currency',
                entry.normalizedValue,
                mergedSettings,
                stickyEditOptions,
            );
        } else if (entry.field.key === 'markupActual') {
            // Scale current selling price by old→new adaos. Clearing sticky and
            // recomputing from entry cost collapses prices when entry data is incomplete.
            applyBulkMarkupActualKeepingListPrice(
                productBuilderForm,
                item,
                entry.normalizedValue,
            );
        } else if (BULK_PRICING_FIELD_EDIT_KEYS.has(entry.field.key)) {
            applyFieldToBuilderForm(
                productBuilderForm,
                entry.field,
                entry.normalizedValue,
            );
            applyPricingFieldEdit(
                productBuilderForm,
                entry.field.key,
                entry.normalizedValue,
                mergedSettings,
                stickyEditOptions,
            );
        } else {
            applyFieldToBuilderForm(
                productBuilderForm,
                entry.field,
                entry.normalizedValue,
            );
        }
        if (entry.field.markupRecalcPolicyA) {
            applyMarkupRecalcPolicyA(productBuilderForm);
        }
    }

    appendEffectiveNoteToForm(productBuilderForm, effectiveNote);

    persistActiveFurnizorPricingFromRoot(productBuilderForm);

    const calculatorInput = builderFormToCalculatorInput(productBuilderForm);
    let computedProduct = calculateProductPricing(calculatorInput, mergedSettings);

    // Keep product code stable for updates.
    if (item.product_code) {
        productBuilderForm.overrideProductCode = true;
        productBuilderForm.overrideProductCodeValue = item.product_code;
        computedProduct.productCode = item.product_code;
    }

    // When supplier entry data is missing, the calculator yields 0. Fall back to the
    // current list price (scaled by old→new markup when both are known).
    if (
        needsPricingRecalc
        && parseNumericInput(computedProduct.listPriceRonIncVatFinal) <= 0
    ) {
        const currentPrice = parseNumericInput(item.price);
        if (currentPrice <= 0) {
            return {
                ok: false,
                reason: 'Lipsă preț furnizor / preț listă — nu se poate recalcula.',
                item,
            };
        }
        let nextPrice = currentPrice;
        if (markupPolicyEdit) {
            const oldMarkupRatio = resolveItemMarkupRatio(item);
            const newMarkupRatio = percentageInputToRatio(markupPolicyEdit.normalizedValue);
            if (oldMarkupRatio != null && oldMarkupRatio > -0.999999) {
                nextPrice = roundMoney(
                    currentPrice * ((1 + newMarkupRatio) / (1 + oldMarkupRatio)),
                    2,
                );
            }
        }
        computedProduct = {
            ...computedProduct,
            listPriceRonIncVatFinal: nextPrice,
        };
    }

    if (needsPricingRecalc) {
        productBuilderForm.listPriceRonIncVatReference = formatNumericInputValue(
            computedProduct.listPriceRonIncVatFinal,
            2,
        );
    }

    const categoryAssignments = buildCategoryAssignmentsFromSystems(
        Array.isArray(productBuilderForm.systems) ? productBuilderForm.systems : [],
        productBuilderForm.systemSubcategories || [],
    );

    const resolvedPriceListIds = priceListIds.length
        ? priceListIds
        : [];

    const payload = builderFormToSavePayload({
        form: productBuilderForm,
        settings: mergedSettings,
        computedProduct,
        categories: categoryAssignments.map((assignment) => assignment.main_category),
        categoryAssignments,
        priceListIds: resolvedPriceListIds,
        oldProductCode: item.product_code,
    });

    const discountableEdit = editsToApply.find(
        (entry) => entry.field.key === 'isDiscountableProduct',
    );
    if (discountableEdit) {
        payload.is_discountable_product = Boolean(discountableEdit.normalizedValue);
    }

    const markupActualEdit = editsToApply.find(
        (entry) => entry.field.key === 'markupActual',
    );
    if (markupActualEdit) {
        // builderFormToSavePayload recomputes from the default tab and can drift
        // sticky list prices by ±0.01 vs preț vânzare — re-lock intentionally.
        const oldRatio = resolveItemMarkupActualRatio(item)
            ?? (
                String(productBuilderForm?.markupActual ?? '').trim() !== ''
                    ? percentageInputToRatio(productBuilderForm.markupActual)
                    : null
            );
        const newRatio = percentageInputToRatio(markupActualEdit.normalizedValue);
        let lockPrice = null;
        if (markupActualPercentsEqual(oldRatio, newRatio)) {
            lockPrice = parseNumericInput(item.price);
        } else if (productBuilderForm.overrideListPriceRonIncVatFinal) {
            lockPrice = parseNumericInput(
                productBuilderForm.overrideListPriceRonIncVatFinalValue,
            );
        } else {
            lockPrice = parseNumericInput(computedProduct.listPriceRonIncVatFinal);
        }
        if (lockPrice > 0) {
            lockPayloadListPrice(payload, lockPrice);
        }
    } else if (!needsPricingRecalc) {
        // Keep existing list price when only metadata changes.
        payload.price = item.price;
        if (payload.pricing_detail) {
            payload.pricing_detail.listPriceRonIncVatFinal = item.price;
            if (Array.isArray(payload.pricing_detail.furnizorPricings)) {
                payload.pricing_detail.furnizorPricings = payload.pricing_detail.furnizorPricings.map(
                    (row) => ({
                        ...row,
                        listPriceRonIncVatFinal: row.isDefault
                            ? item.price
                            : row.listPriceRonIncVatFinal,
                    }),
                );
            }
        }
    }

    const previousValueLabel = editsToApply
        .map((entry) => {
            let label;
            if (entry.field?.isListExchangeRate) {
                const fromSettings = previousSettings[entry.field.key];
                label = entry.field.formatDisplay
                    ? entry.field.formatDisplay(fromSettings)
                    : String(fromSettings ?? '—');
            } else {
                label = formatCurrentFieldValue(entry.field, item);
            }
            return editsToApply.length > 1
                ? `${entry.field.label}: ${label}`
                : label;
        })
        .join('; ') || (String(effectiveNote || '').trim() ? '—' : '');
    const nextValueLabel = editsToApply
        .map((entry) => {
            let label;
            if (
                entry.applyMode === 'multiplyPercent'
                && entry.field?.key === 'supplierListPriceNew'
            ) {
                // Show calculated new supplier list price + currency/U.M. + %.
                const percentLabel = formatMultiplyPercentPreviewLabel(entry.value);
                label = formatSupplierListPriceBulkLabel(
                    productBuilderForm.supplierListPriceNew,
                    item,
                    { percentLabel },
                );
            } else if (entry.applyMode === 'multiplyPercent') {
                label = formatMultiplyPercentPreviewLabel(entry.value);
            } else if (entry.applyMode === 'costUpliftFixed') {
                label = formatCostUpliftFixedPreviewLabel(entry.value);
            } else {
                label = formatBulkEditValue(entry.field, entry.value);
            }
            return editsToApply.length > 1
                ? `${entry.field.label}: ${label}`
                : label;
        })
        .join('; ');
    const noteLabel = String(effectiveNote || '').trim();
    const previousPrice = item.price;
    const nextPrice = payload.price;

    const builderState = {
        productBuilderForm: cloneDeep(productBuilderForm),
        pricingSettings: cloneDeep(mergedSettings),
        ...bulkDraftBaselines,
    };
    // Mark FX-recalc drafts so cancel can restore the rates products were on
    // before this FX wave. Keep the first previous for the whole pending wave
    // (later FX saves must not move the revert target to an intermediate header).
    if (exchangeRateEdits.length) {
        builderState.exchangeRateRecalc = true;
        builderState.exchangeRateRecalcPrevious =
            exchangeRateRecalcPreviousLock
            || existingBuilderState?.exchangeRateRecalcPrevious
            || {
                exchangeRateEur: previousSettings.exchangeRateEur,
                exchangeRatePln: previousSettings.exchangeRatePln,
                exchangeRateUsd: previousSettings.exchangeRateUsd,
            };
    }

    return {
        ok: true,
        payload,
        builderState,
        preview: {
            product_code: item.product_code,
            product_name: item.product_name || item.name,
            previous_value: previousValueLabel || '—',
            next_value: [nextValueLabel, noteLabel].filter(Boolean).join('; ') || '—',
            previous_price: previousPrice,
            next_price: nextPrice,
            price_changed: Number(previousPrice) !== Number(nextPrice),
        },
        item,
        hasMarkupPolicyA,
        pricingSettingsPatch: exchangeRateEdits.length
            ? Object.fromEntries(
                exchangeRateEdits.map((entry) => [
                    entry.field.key,
                    parseNumericInput(entry.normalizedValue ?? entry.value),
                ]).filter(([, rate]) => rate > 0),
            )
            : null,
    };
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatMultiplyPercentPreviewLabel(value) {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return '—';
    }
    if (raw.startsWith('+') || raw.startsWith('-')) {
        return `${raw}%`;
    }
    return `+${raw}%`;
}

/**
 * Preview label for Majorare costuri as fixed RON/buc (no %).
 * @param {unknown} value
 * @returns {string}
 */
function formatCostUpliftFixedPreviewLabel(value) {
    const raw = String(value ?? '').trim();
    if (!raw) {
        return '—';
    }
    if (raw.startsWith('+') || raw.startsWith('-')) {
        return raw;
    }
    return `+${raw}`;
}

/**
 * @param {Object} params
 * @param {Array<Object>} params.items
 * @param {string} [params.fieldKey]
 * @param {unknown} [params.value]
 * @param {Array<{ fieldKey: string, value: unknown }>} [params.edits]
 * @param {Array<Object|number>} params.priceListIds
 * @param {Object} [params.pricingSettings]
 * @returns {{
 *   results: Array<Object>,
 *   skipped: Array<{ item: Object, reason: string }>,
 *   drafts: Array<Object>,
 *   payloads: Array<Object>,
 * }}
 */
export function buildBulkPayloads({
    items,
    fieldKey,
    value,
    edits = null,
    priceListIds,
    pricingSettings,
    effectiveNote = '',
    furnizorLabel = '',
    exchangeRateRecalcPreviousLock = null,
}) {
    const field = fieldKey ? getBulkField(fieldKey) : null;
    const results = [];
    const skipped = [];
    const drafts = [];
    const payloads = [];
    const existingCodes = (items || []).map((item) => item.product_code);

    for (const item of items || []) {
        const built = buildBulkUpdateForItem(item, field, value, {
            priceListIds,
            pricingSettings,
            edits,
            effectiveNote,
            furnizorLabel,
            exchangeRateRecalcPreviousLock,
        });
        if (!built.ok) {
            skipped.push({ item, reason: built.reason });
            continue;
        }
        results.push(built);
        payloads.push(built.payload);
        const action = resolveDraftAction(built.payload, existingCodes);
        const entry = createDraftEntry({
            action,
            payload: built.payload,
            builderState: built.builderState,
            sourceProductCode: item.product_code,
            previousPrice: item.price,
            updateMode: 'builder',
        });
        if (item._draftId) {
            entry.id = item._draftId;
        }
        drafts.push(entry);
    }

    return { results, skipped, drafts, payloads };
}

/**
 * Preview rows for the bulk dialog.
 * @param {Object} params
 * @returns {{ rows: Array<Object>, skipped: Array<Object>, field: Object|null, payloads: Array<Object>, drafts: Array<Object> }}
 */
export function buildBulkPreview({
    items,
    fieldKey,
    value,
    edits = null,
    priceListIds,
    pricingSettings,
    effectiveNote = '',
    furnizorLabel = '',
}) {
    const field = fieldKey ? getBulkField(fieldKey) : (
        Array.isArray(edits) && edits[0]?.fieldKey
            ? getBulkField(edits[0].fieldKey)
            : null
    );
    const { results, skipped, payloads, drafts } = buildBulkPayloads({
        items,
        fieldKey,
        value,
        edits,
        priceListIds,
        pricingSettings,
        effectiveNote,
        furnizorLabel,
    });
    return {
        field,
        rows: results.map((entry) => entry.preview),
        skipped,
        payloads,
        drafts,
        hasMarkupPolicyA: results.some((entry) => entry.hasMarkupPolicyA),
        pricingSettingsPatch: collectPricingSettingsPatch(results),
    };
}

/**
 * Merge per-item pricingSettingsPatch objects from bulk results.
 * @param {Array<{ pricingSettingsPatch?: Record<string, number>|null }>} results
 * @returns {Record<string, number>|null}
 */
function collectPricingSettingsPatch(results) {
    const merged = {};
    for (const entry of results || []) {
        const patch = entry?.pricingSettingsPatch;
        if (!patch || typeof patch !== 'object') {
            continue;
        }
        Object.assign(merged, patch);
    }
    return Object.keys(merged).length ? merged : null;
}

/** @type {Record<string, string>} */
const EXCHANGE_RATE_FIELD_TO_CURRENCY = {
    exchangeRateEur: 'euro',
    exchangeRatePln: 'pln',
    exchangeRateUsd: 'usd',
};

/**
 * Currencies whose selling price depends on the given list FX field keys.
 * @param {Iterable<string>} rateFieldKeys
 * @returns {Set<string>}
 */
export function currenciesForExchangeRateFields(rateFieldKeys) {
    const currencies = new Set();
    for (const key of rateFieldKeys || []) {
        const currency = EXCHANGE_RATE_FIELD_TO_CURRENCY[key];
        if (currency) {
            currencies.add(currency);
        }
    }
    return currencies;
}

/**
 * Working currencies used by a product (root + furnizor tabs).
 * @param {Object} item
 * @returns {Set<string>}
 */
export function collectItemWorkingCurrencies(item) {
    const currencies = new Set();
    const detail = item?.pricing_detail || {};
    const push = (raw) => {
        // Do not call normalizeCurrencyCode on empty — it defaults to `euro`.
        if (raw === null || raw === undefined || String(raw).trim() === '') {
            return;
        }
        const code = normalizeCurrencyCode(raw);
        if (code && code !== 'ron') {
            currencies.add(code);
        }
    };
    push(detail.currency);
    push(item?.currency);
    const rows = Array.isArray(detail.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    rows.forEach((row) => push(row?.currency));
    return currencies;
}

/**
 * True when the product uses at least one of the affected FX currencies.
 * @param {Object} item
 * @param {Set<string>} affectedCurrencies
 * @returns {boolean}
 */
export function itemAffectedByExchangeRateCurrencies(item, affectedCurrencies) {
    if (!(affectedCurrencies instanceof Set) || !affectedCurrencies.size) {
        return false;
    }
    const used = collectItemWorkingCurrencies(item);
    for (const currency of used) {
        if (affectedCurrencies.has(currency)) {
            return true;
        }
    }
    return false;
}

/**
 * Overlay live catalog items with lista-în-lucru drafts so FX recalc uses draft
 * builder values. Optionally also include create-only drafts not on the list.
 *
 * @param {Array<Object>} items
 * @param {Array<Object>} drafts
 * @param {{ includeOrphanDrafts?: boolean }} [options]
 * @returns {Array<Object>}
 */
export function resolveItemsWithDraftOverlay(items = [], drafts = [], options = {}) {
    const includeOrphanDrafts = options.includeOrphanDrafts !== false;
    const list = Array.isArray(items) ? items : [];
    const draftList = Array.isArray(drafts) ? drafts : [];
    const codes = new Set();
    const resolved = [];

    const pushResolved = (productCode, fallback = null) => {
        const code = String(productCode || '').trim();
        if (!code || codes.has(code)) {
            return;
        }
        codes.add(code);
        const peer = resolvePropagatePeerItem({
            productCode: code,
            items: list,
            drafts: draftList,
        });
        if (peer) {
            resolved.push(peer);
            return;
        }
        if (fallback) {
            resolved.push(fallback);
        }
    };

    for (const item of list) {
        pushResolved(item?.product_code, item);
    }
    if (includeOrphanDrafts) {
        for (const draft of draftList) {
            pushResolved(
                draft?.payload?.product_code || draft?.sourceProductCode,
            );
        }
    }

    return resolved;
}

/**
 * Recalculate products for changed list exchange rates, keeping adaos actual.
 * Only products in the currencies of the rates that actually changed are
 * processed; only drafts where selling price changes are returned.
 *
 * When `drafts` are provided, existing lista-în-lucru values are used as the
 * recalc base (same policy as furnizor propagate) instead of live catalog.
 *
 * @param {Object} params
 * @param {Array<Object>} params.items
 * @param {Object} params.previousPricingSettings
 * @param {Object} params.nextPricingSettings
 * @param {Array<Object|number>} params.priceListIds
 * @param {Array<Object>} [params.drafts] existing lista-în-lucru entries
 * @param {boolean} [params.includeOrphanDrafts=true]
 *   When false (chunked live scans), only overlay drafts onto `items` —
 *   create-only drafts are handled in a separate pass.
 * @returns {{
 *   drafts: Array<Object>,
 *   skipped: Array<{ item: Object, reason: string }>,
 *   changedCount: number,
 * }}
 */
export function buildExchangeRateRecalcDrafts({
    items,
    previousPricingSettings,
    nextPricingSettings,
    priceListIds = [],
    drafts: existingDrafts = [],
    includeOrphanDrafts = true,
    /** Baseline FX (appliedExchangeRates) — cancel restores to these. */
    appliedExchangeRates = null,
}) {
    const prev = {
        ...DEFAULT_PRICING_SETTINGS,
        ...previousPricingSettings,
    };
    const next = {
        ...DEFAULT_PRICING_SETTINGS,
        ...nextPricingSettings,
    };
    const edits = [];
    for (const key of ['exchangeRateEur', 'exchangeRatePln', 'exchangeRateUsd']) {
        if (parseNumericInput(prev[key]) === parseNumericInput(next[key])) {
            continue;
        }
        edits.push({
            fieldKey: key,
            value: String(next[key]),
        });
    }
    if (!edits.length) {
        return { drafts: [], skipped: [], changedCount: 0 };
    }

    const affectedCurrencies = currenciesForExchangeRateFields(
        edits.map((entry) => entry.fieldKey),
    );
    const resolvedItems = resolveItemsWithDraftOverlay(
        items,
        existingDrafts,
        { includeOrphanDrafts },
    );
    const relevantItems = resolvedItems.filter((item) =>
        itemAffectedByExchangeRateCurrencies(item, affectedCurrencies),
    );

    const appliedLock = appliedExchangeRates
        ? {
            exchangeRateEur: appliedExchangeRates.exchangeRateEur,
            exchangeRatePln: appliedExchangeRates.exchangeRatePln,
            exchangeRateUsd: appliedExchangeRates.exchangeRateUsd,
        }
        : null;

    const { results, skipped, drafts } = buildBulkPayloads({
        items: relevantItems,
        edits,
        priceListIds,
        // Pass previous settings; exchange edits overwrite rates inside builder.
        pricingSettings: prev,
        // Prefer list baseline, then any stamp already on pending FX drafts.
        exchangeRateRecalcPreviousLock:
            appliedLock
            || resolveExchangeRateRecalcPrevious(existingDrafts),
    });

    const changedDrafts = [];
    results.forEach((entry, index) => {
        if (!entry?.preview?.price_changed) {
            return;
        }
        if (drafts[index]) {
            changedDrafts.push(drafts[index]);
        }
    });

    return {
        drafts: changedDrafts,
        skipped,
        changedCount: changedDrafts.length,
    };
}
